import { DataTable } from "@components/DataTable/DataTable"
import type { IAccount } from "@apis/accounts/types"
import type { ColumnDef } from "@tanstack/react-table"
import { DateCell } from "@utils/table/columnFactories"
import { Link } from "react-router"
import { Flame, Zap } from "lucide-react"
import type { IAddress } from "@commonTypes/address"
import { Button } from "@components/ui/button"
import { DelinkAccount } from "@components/MembershipProfileNew/DelinkAccountConfirmation"
import { useDrawer } from "@contexts/DrawerContext"
import { useSortingState } from "@hooks/useSortingState"

type TMembershipProfileAccountsProps = {
  accounts: IAccount[]
  addresses: IAddress[]
}

export const MembershipProfileAccounts = ({
  accounts,
  addresses,
}: TMembershipProfileAccountsProps) => {
  const { openDrawer } = useDrawer()
  const sorting = useSortingState([{ id: "created_at", desc: true }])

  const tableColumns: ColumnDef<IAccount>[] = [
    {
      id: "product_type_code",
      cell: ({ row }) => {
        const rowData = row.original
        return (
          <div>
            {rowData.product_type_code === "NaturalGas" ? (
              <Flame size="24" />
            ) : (
              <Zap size="24" />
            )}
          </div>
        )
      },
    },
    {
      header: "ID",
      accessorKey: "account_id",
    },
    {
      header: "Utility Account Name",
      accessorKey: "utility_full_name",
    },
    {
      accessorKey: "market_code",
      header: "Utility",
    },
    {
      accessorKey: "account_number",
      header: "Account Number",
      cell: ({ row }) => {
        const rowData = row.original
        const href = `/profiles/${rowData.membership_profile_id}/accounts/${rowData.account_id}`

        return (
          <Button variant="link">
            <Link to={href}>{row.original.account_number}</Link>
          </Button>
        )
      },
    },
    {
      id: "service_address_id",
      header: "Service Address",
      cell: ({ row }) => {
        const address = addresses.find(
          (a) => a.address_id === row.original.service_address_id,
        )

        return address ? (
          <span>
            {address.street}
            {address.unit && ` ${address.unit}`}, {address.city},{" "}
            {address.state_code} {address.zip}
          </span>
        ) : (
          "Address not found"
        )
      },
    },
    {
      accessorKey: "status_group",
      header: "Status",
    },
    {
      accessorKey: "sale_dt",
      header: "Sold",
      cell: (props) => <DateCell {...props} />,
    },
    {
      id: "actions",
      cell: ({ row }) =>
        accounts.length > 1 && (
          <Button
            variant="destructive"
            onClick={() => {
              openDrawer(
                <DelinkAccount account_id={String(row.original.account_id)} />,
                "Delink Account",
              )
            }}
          >
            Delink Account
          </Button>
        ),
    },
  ]

  return (
    <div>
      {accounts.length ? (
        <DataTable
          columns={tableColumns}
          data={accounts}
          classes="tw:w-full"
          {...sorting}
        />
      ) : (
        <h2>No Other Accounts</h2>
      )}
    </div>
  )
}
